import * as THREE from 'three';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { colored } from '../../Style.js';
import { SCIFI, armor, glowColor, glowMaterial, bake, prism, hexColor } from './units-style.js';

const NEUTRAL_RING = 0x9aa3ad;
const DECK_HEIGHT = 1.8;

// A pulse turret: a finned tech pylon whose bands and panels light up in the
// owner's colour, under a rotating sensor head with twin pulse barrels.
// Unclaimed turrets are bare white alloy with a dark, idle sensor.
export class Turret {
  constructor(scene, turret) {
    this.scene = scene;
    this.turret = turret;
    this.meshGroup = new THREE.Group();
    this.headGroup = null; // Rotating part (sensor head + barrels)
    this.baseMesh = null;
    this.headMesh = null; // Sensor lens, pulses while tracking
    this.gunMesh = null;
    this.rangeMesh = null;
    this.teamMaterial = null; // Lit team panels
    this.glowMaterial = null; // Unlit team bands
    this.holoRing = null;
    this.targetRotation = 0;
    this.currentRotation = 0;
    this.create();
  }

  create() {
    const neutral = this.turret.ownerId === -1;

    this.teamMaterial = new THREE.MeshStandardMaterial({
      color: this.getPanelColor(),
      roughness: 0.5,
      metalness: 0.3,
      flatShading: true,
      emissive: this.getColor(),
      emissiveIntensity: neutral ? 0 : 0.25
    });
    this.glowMaterial = glowMaterial({ color: this.getGlowColor() });

    // Pylon: tiered plinth, tapering core and four buttress fins (stationary)
    this.baseMesh = new THREE.Mesh(bake(this.buildPylon()), armor());
    this.baseMesh.castShadow = true;
    this.baseMesh.receiveShadow = true;
    this.meshGroup.add(this.baseMesh);

    const panels = new THREE.Mesh(bake(this.buildPanels()), this.teamMaterial);
    panels.castShadow = true;
    this.meshGroup.add(panels);
    this.meshGroup.add(new THREE.Mesh(bake(this.buildBands()), this.glowMaterial));

    // Broken holo ring circling the pylon
    const arcs = [];
    for (let i = 0; i < 3; i++) {
      arcs.push(new THREE.TorusGeometry(2.7, 0.05, 3, 12, 1.5).rotateZ(i * Math.PI * 2 / 3));
    }
    this.holoRing = new THREE.Mesh(bake(arcs.map(a => colored(a, 0xffffff))), this.glowMaterial);
    this.holoRing.rotation.x = -Math.PI / 2;
    this.holoRing.position.y = 1.0;
    this.meshGroup.add(this.holoRing);

    // Rotating head: turntable, armoured sensor housing and barrels, aimed along +Z
    this.headGroup = new THREE.Group();
    this.headGroup.position.y = DECK_HEIGHT;
    this.meshGroup.add(this.headGroup);

    const head = new THREE.Mesh(bake(this.buildHead()), armor());
    head.castShadow = true;
    this.headGroup.add(head);

    // Armoured cap and cheek plates in the owner's colour, so ownership reads from above
    const cap = new THREE.Mesh(bake([
      colored(prism([[-0.9, 0.62], [1.12, 0.62], [0.72, 1.16], [-0.82, 1.12]], 1.6), 0xffffff),
      colored(new THREE.BoxGeometry(0.1, 0.4, 1.3), 0xffffff, { x: -0.8, y: 0.42, z: -0.05 }),
      colored(new THREE.BoxGeometry(0.1, 0.4, 1.3), 0xffffff, { x: 0.8, y: 0.42, z: -0.05 })
    ]), this.teamMaterial);
    cap.castShadow = true;
    this.headGroup.add(cap);
    this.headGroup.add(new THREE.Mesh(bake([
      colored(new THREE.CylinderGeometry(1.17, 1.17, 0.06, 8), 0xffffff, { y: 0.22, ry: Math.PI / 8 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.08), 0xffffff, { x: 0.5, y: 1.85, z: -0.6 })
    ]), this.glowMaterial));

    // Twin pulse barrels, which draw back as the turret locks on
    this.gunMesh = new THREE.Mesh(bake(this.buildBarrels()), armor());
    this.gunMesh.castShadow = true;
    this.headGroup.add(this.gunMesh);
    const coils = [];
    for (const sx of [-1, 1]) {
      for (const z of [1.55, 1.9]) {
        coils.push(colored(new THREE.CylinderGeometry(0.15, 0.15, 0.08, 6), 0xffffff, { x: sx * 0.36, y: 0.5, z, rx: Math.PI / 2 }));
      }
      coils.push(colored(new THREE.BoxGeometry(0.14, 0.14, 0.04), 0xffffff, { x: sx * 0.36, y: 0.5, z: 2.37 }));
    }
    this.gunMesh.add(new THREE.Mesh(bake(coils), this.glowMaterial));

    // Sensor lens on the sloped front of the head
    const lensMaterial = new THREE.MeshStandardMaterial({
      color: this.getLensColor(),
      roughness: 0.3,
      metalness: 0.2,
      flatShading: true,
      emissive: this.getColor(),
      emissiveIntensity: neutral ? 0 : 0.6
    });
    this.headMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.32, 0.1, 8), lensMaterial);
    this.headMesh.rotation.x = 0.9;
    this.headMesh.position.set(0, 0.9, 0.97);
    this.headGroup.add(this.headMesh);

    // Range indicator (semi-transparent ring)
    const rangeGeometry = new THREE.RingGeometry(
      this.turret.claimRadius - 0.3,
      this.turret.claimRadius,
      32
    );
    const rangeMaterial = new THREE.MeshBasicMaterial({
      color: this.getRingColor(),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.3
    });
    this.rangeMesh = new THREE.Mesh(rangeGeometry, rangeMaterial);
    this.rangeMesh.rotation.x = -Math.PI / 2;
    this.rangeMesh.position.y = 0.05;
    this.meshGroup.add(this.rangeMesh);

    // Position the group
    this.meshGroup.position.set(
      this.turret.position.x,
      this.turret.position.y,
      this.turret.position.z
    );

    this.scene.add(this.meshGroup);
  }

  buildPylon() {
    const parts = [
      colored(new THREE.CylinderGeometry(2.3, 2.5, 0.36, 8), SCIFI.gunmetal, { y: 0.18, ry: Math.PI / 8 }),
      colored(new THREE.CylinderGeometry(2.0, 2.2, 0.22, 8), SCIFI.alloyDark, { y: 0.47, ry: Math.PI / 8 }),
      colored(new THREE.CylinderGeometry(1.2, 1.55, 1.2, 8), SCIFI.white, { y: 1.15, ry: Math.PI / 8 }),
      colored(new THREE.CylinderGeometry(1.45, 1.3, 0.16, 8), SCIFI.gunmetal, { y: DECK_HEIGHT - 0.08, ry: Math.PI / 8 })
    ];
    // Buttress fins on the diagonals
    for (let i = 0; i < 4; i++) {
      const fin = colored(prism([[1.1, 0.55], [2.2, 0.55], [2.2, 0.8], [1.3, 1.65], [1.1, 1.65]], 0.36), SCIFI.alloy);
      parts.push(fin.rotateY(Math.PI / 4 + i * Math.PI / 2));
    }
    // Panel seams around the plinth
    for (let i = 0; i < 8; i++) {
      parts.push(colored(new THREE.BoxGeometry(0.05, 0.37, 0.06), SCIFI.black, { z: 2.36 }).rotateY(i * Math.PI / 4));
    }
    return parts;
  }

  // Team panels on the four faces of the pylon core, leaning with its taper
  buildPanels() {
    const parts = [];
    for (let i = 0; i < 4; i++) {
      parts.push(colored(new THREE.BoxGeometry(0.8, 0.8, 0.08), 0xffffff, { y: 1.1, z: 1.33, rx: -0.28 }).rotateY(i * Math.PI / 2));
    }
    return parts;
  }

  // Glowing bands: around the plinth, under the deck and down each fin
  buildBands() {
    const parts = [
      colored(new THREE.CylinderGeometry(2.33, 2.33, 0.06, 8), 0xffffff, { y: 0.32, ry: Math.PI / 8 }),
      colored(new THREE.CylinderGeometry(1.47, 1.47, 0.05, 8), 0xffffff, { y: DECK_HEIGHT - 0.2, ry: Math.PI / 8 })
    ];
    for (let i = 0; i < 4; i++) {
      parts.push(colored(new THREE.BoxGeometry(0.1, 0.06, 1.05), 0xffffff, { y: 1.25, z: 1.79, rx: 0.78 })
        .rotateY(Math.PI / 4 + i * Math.PI / 2));
    }
    return parts;
  }

  buildHead() {
    return [
      colored(new THREE.CylinderGeometry(1.1, 1.2, 0.22, 8), SCIFI.gunmetal, { y: 0.11, ry: Math.PI / 8 }),
      colored(prism([[-0.95, 0.22], [0.8, 0.22], [1.1, 0.62], [-0.9, 0.62]], 1.5), SCIFI.white),
      colored(new THREE.BoxGeometry(1.64, 0.05, 0.05), SCIFI.black, { y: 0.92, z: -0.3 }),
      colored(new THREE.BoxGeometry(1.62, 0.06, 2.0), SCIFI.gunmetal, { y: 0.62, z: 0.08 }),
      // Rear heat sink fins and antenna
      colored(new THREE.BoxGeometry(1.1, 0.5, 0.3), SCIFI.alloyDark, { y: 0.6, z: -1.05 }),
      colored(new THREE.CylinderGeometry(0.03, 0.03, 0.75, 4), SCIFI.alloy, { x: 0.5, y: 1.45, z: -0.6 })
    ];
  }

  buildBarrels() {
    const parts = [];
    for (const sx of [-1, 1]) {
      const x = sx * 0.36;
      parts.push(colored(new THREE.BoxGeometry(0.34, 0.34, 0.6), SCIFI.gunmetal, { x, y: 0.5, z: 1.05 }));
      parts.push(colored(new THREE.CylinderGeometry(0.09, 0.11, 1.3, 6), SCIFI.alloyDark, { x, y: 0.5, z: 1.75, rx: Math.PI / 2 }));
      parts.push(colored(new THREE.BoxGeometry(0.26, 0.26, 0.18), SCIFI.gunmetal, { x, y: 0.5, z: 2.3 }));
    }
    return parts;
  }

  getColor() {
    if (this.turret.ownerId === -1) {
      return 0x888888; // Gray for unclaimed
    }
    return hexColor(PLAYER_COLORS[this.turret.ownerId]);
  }

  getPanelColor() {
    return this.turret.ownerId === -1 ? SCIFI.neutralPanel : this.getColor();
  }

  getGlowColor() {
    return this.turret.ownerId === -1 ? new THREE.Color(SCIFI.neutralGlow) : glowColor(this.getColor());
  }

  getLensColor() {
    return this.turret.ownerId === -1 ? SCIFI.black : this.getColor();
  }

  getRingColor() {
    return this.turret.ownerId === -1 ? NEUTRAL_RING : this.getColor();
  }

  update(turretData, time, units = []) {
    const ownerChanged = !this.turret || this.turret.ownerId !== turretData.ownerId;
    this.turret = turretData;
    const neutral = this.turret.ownerId === -1;

    // Repaint the panels and bands if the owner changed
    if (ownerChanged) {
      const color = this.getColor();
      this.teamMaterial.color.setHex(this.getPanelColor());
      this.teamMaterial.emissive.setHex(color);
      this.teamMaterial.emissiveIntensity = neutral ? 0 : 0.25;
      this.glowMaterial.color.copy(this.getGlowColor());
      if (this.headMesh) {
        this.headMesh.material.color.setHex(this.getLensColor());
        this.headMesh.material.emissive.setHex(color);
      }
      if (this.rangeMesh) {
        this.rangeMesh.material.color.setHex(this.getRingColor());
      }
    }

    // Handle destroyed state
    if (this.turret.isDestroyed) {
      this.meshGroup.visible = false;
    } else {
      this.meshGroup.visible = true;

      // Holo ring drifts around the pylon; it idles slowly when unclaimed
      this.holoRing.rotation.z = time * (neutral ? 0.3 : 0.8);
      this.holoRing.position.y = 1.0 + Math.sin(time * 1.5) * 0.2;

      // Find nearest enemy and rotate toward them
      if (!neutral && this.headGroup) {
        const nearestEnemy = this.findNearestEnemy(units);
        if (nearestEnemy) {
          // Calculate angle to enemy
          const dx = nearestEnemy.position.x - this.turret.position.x;
          const dz = nearestEnemy.position.z - this.turret.position.z;
          this.targetRotation = Math.atan2(dx, dz);
        }

        // Smoothly interpolate rotation
        let rotationDiff = this.targetRotation - this.currentRotation;
        // Normalize to [-PI, PI]
        while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
        while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
        this.currentRotation += rotationDiff * 0.1;

        this.headGroup.rotation.y = this.currentRotation;

        // Visual feedback for tracking state
        if (this.headMesh && this.turret.isTracking) {
          // Pulse the lens while tracking, brighter as it locks on
          const baseIntensity = 0.6;
          const trackingPulse = Math.sin(time * 8) * 0.25;
          const lockOnBoost = this.turret.trackingProgress * 1.2;
          this.headMesh.material.emissiveIntensity = baseIntensity + trackingPulse + lockOnBoost;
          // Draw the barrels back as it charges
          if (this.gunMesh) this.gunMesh.position.z = -this.turret.trackingProgress * 0.3;
        } else if (this.headMesh) {
          this.headMesh.material.emissiveIntensity = 0.6;
          if (this.gunMesh) this.gunMesh.position.z = 0;
        }
      } else if (neutral && this.headGroup) {
        // Slow idle sweep for unclaimed turrets
        this.headGroup.rotation.y = time * 0.2;
        if (this.headMesh) this.headMesh.material.emissiveIntensity = 0;
      }

      // Pulse effect for unclaimed turrets
      if (neutral && this.rangeMesh) {
        const pulse = 0.2 + Math.sin(time * 2) * 0.1;
        this.rangeMesh.material.opacity = pulse;
      } else if (this.rangeMesh) {
        this.rangeMesh.material.opacity = 0.3;
      }
    }
  }

  // Find nearest enemy unit within attack range
  findNearestEnemy(units) {
    if (!units || units.length === 0) return null;

    const attackRange = 20; // Match server TurretAttackRange (~4 squares)
    let nearest = null;
    let nearestDist = attackRange + 1;

    for (const unit of units) {
      // Only target enemy units
      if (unit.ownerId === this.turret.ownerId) continue;
      // Skip dead/respawning units
      if (unit.isRespawning) continue;

      const dx = unit.position.x - this.turret.position.x;
      const dz = unit.position.z - this.turret.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = unit;
      }
    }

    return nearest;
  }

  // Check if player is in claiming range
  isInRange(position) {
    const dx = position.x - this.turret.position.x;
    const dz = position.z - this.turret.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.turret.claimRadius * this.turret.claimRadius;
  }

  // Check if turret can be claimed by player
  canBeClaimed(playerId) {
    if (this.turret.isDestroyed) return false;
    if (this.turret.ownerId === playerId) return false;
    return true;
  }

  remove() {
    this.scene.remove(this.meshGroup);
    this.meshGroup.traverse(child => {
      if (child.isMesh) child.geometry.dispose();
    });
    // The armour material is shared; the team, lens and ring materials are ours
    this.teamMaterial.dispose();
    this.glowMaterial.dispose();
    this.headMesh.material.dispose();
    this.rangeMesh.material.dispose();
  }
}
